import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";
import { Gift, DollarSign, Info } from "lucide-react";

// Restored 2026-09-13 — this screen existed live at
// xgocontrolcenter.softworkscapital.com/enhanced-promotions but was missing from this checkout
// (no file, no route, not in git history). Rebuilt from live screenshots against the real,
// already-working backend (routes/promotions.js) — same promo_budgets/promo_configurations
// schema already confirmed correct elsewhere in this session. Fixes one real bug found in the
// process: the original "Edit Configuration" modal left Configuration Type as a blank, editable
// text field — since PUT /promotions/configurations/:promo_id/:config_type keys off that URL
// param, a blank/mistyped value there would silently hit the wrong (or no) row. Made read-only
// here, prefilled from the row being edited.

function formatMoney(n) {
  return `$${(Number(n) || 0).toFixed(2)}`;
}

function EditConfigModal({ config, onClose, onSaved }) {
  const [rewardAmount, setRewardAmount] = useState(config.reward_amount ?? 0);
  const [isActive, setIsActive] = useState(!!config.is_active);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${API_URL}/promotions/configurations/${encodeURIComponent(config.promo_id)}/${encodeURIComponent(config.config_type)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rewardAmount: parseFloat(rewardAmount), isActive }),
        }
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || "Failed to save");
      onSaved(data.data);
    } catch (e) {
      alert(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-bold text-gray-800 dark:text-white">Edit Configuration</h3>

        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Promotion ID</label>
        <input disabled value={config.promo_id} className="mb-4 w-full rounded-lg border bg-gray-100 p-2 text-gray-500" />

        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Configuration Type</label>
        <input disabled value={config.config_type} className="mb-4 w-full rounded-lg border bg-gray-100 p-2 text-gray-500" />

        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200">Reward Amount ($)</label>
        <input
          type="number"
          step="0.01"
          value={rewardAmount}
          onChange={(e) => setRewardAmount(e.target.value)}
          className="mb-4 w-full rounded-lg border p-2"
        />

        <label className="mb-4 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          Active
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
          <button
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FundWalletModal({ configs, onClose, onFunded }) {
  const active = configs.filter((c) => c.is_active);
  const [amount, setAmount] = useState("");
  const [source, setSource] = useState("external");
  const [method, setMethod] = useState("equal");
  const [customAmounts, setCustomAmounts] = useState({});
  const [funding, setFunding] = useState(false);

  const totalAmount = parseFloat(amount) || 0;
  const equalShare = active.length > 0 ? totalAmount / active.length : 0;

  const allocations = active.map((c) => {
    const key = `${c.promo_id}/${c.config_type}`;
    const amt = method === "custom" ? parseFloat(customAmounts[key] || 0) : equalShare;
    return { promoId: c.promo_id, configType: c.config_type, amount: amt, label: c.config_type };
  });
  const allocatedTotal = allocations.reduce((s, a) => s + (a.amount || 0), 0);

  const fund = async () => {
    setFunding(true);
    try {
      const res = await fetch(`${API_URL}/promotions/wallet/fund`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: totalAmount,
          source,
          allocations: allocations.map(({ promoId, configType, amount }) => ({ promoId, configType, amount })),
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === "error") throw new Error(data.message || "Failed to fund wallet");
      onFunded();
    } catch (e) {
      alert(e.message);
    } finally {
      setFunding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 py-8">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-bold text-gray-800 dark:text-white">Fund Promotion Wallet</h3>

        <label className="mb-1 block text-sm font-medium">Amount to Add to Wallet</label>
        <input
          type="number"
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="mb-4 w-full rounded-lg border p-2"
        />

        <label className="mb-1 block text-sm font-medium">Funding Source</label>
        <div className="mb-4 grid grid-cols-2 gap-3">
          <label className={`cursor-pointer rounded-lg border p-3 text-sm ${source === "external" ? "border-blue-500" : ""}`}>
            <input type="radio" checked={source === "external"} onChange={() => setSource("external")} className="mr-2" />
            External/Cash — Fund from outside the system
          </label>
          <label className={`cursor-pointer rounded-lg border p-3 text-sm ${source === "revenue" ? "border-blue-500" : ""}`}>
            <input type="radio" checked={source === "revenue"} onChange={() => setSource("revenue")} className="mr-2" />
            Revenue Wallet — Transfer from revenue wallet
          </label>
        </div>

        <label className="mb-1 block text-sm font-medium">Allocation Method</label>
        <div className="mb-4 flex gap-4 text-sm">
          <label><input type="radio" checked={method === "equal"} onChange={() => setMethod("equal")} className="mr-1" />Equal Split</label>
          <label><input type="radio" checked={method === "custom"} onChange={() => setMethod("custom")} className="mr-1" />Custom Amounts</label>
        </div>

        <p className="mb-2 text-sm font-semibold">Budget Allocation for Active Promotions</p>
        <div className="mb-4 max-h-64 space-y-2 overflow-y-auto">
          {active.map((c) => {
            const key = `${c.promo_id}/${c.config_type}`;
            const amt = method === "custom" ? customAmounts[key] || "" : equalShare.toFixed(2);
            return (
              <div key={key} className="flex items-center justify-between rounded-lg border p-3 text-sm">
                <div>
                  <div className="font-medium">{c.config_type}</div>
                  <div className="text-xs text-gray-500">{c.promo_id}</div>
                </div>
                {method === "custom" ? (
                  <input
                    type="number"
                    step="0.01"
                    value={amt}
                    onChange={(e) => setCustomAmounts((prev) => ({ ...prev, [key]: e.target.value }))}
                    className="w-28 rounded border p-1 text-right"
                  />
                ) : (
                  <span className="font-semibold text-blue-600">${amt}</span>
                )}
              </div>
            );
          })}
        </div>

        <div className="mb-4 flex justify-between border-t pt-3 text-sm font-semibold">
          <span>Total Allocation: {formatMoney(allocatedTotal)}</span>
          <span>Remaining: {formatMoney(totalAmount - allocatedTotal)}</span>
        </div>

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
          <button
            onClick={fund}
            disabled={funding || totalAmount <= 0 || Math.abs(allocatedTotal - totalAmount) > 0.01}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {funding ? "Funding..." : "Fund Wallet"}
          </button>
        </div>
      </div>
    </div>
  );
}

const EnhancedPromotions = () => {
  const [walletSummary, setWalletSummary] = useState({ total_transferred: 0, current_balance: 0 });
  const [budgets, setBudgets] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [analytics, setAnalytics] = useState({ usageStats: [], summary: {} });
  const [editingConfig, setEditingConfig] = useState(null);
  const [showFundModal, setShowFundModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/promotions/wallet/summary`).then((r) => (r.ok ? r.json() : null)).catch(() => null),
      fetch(`${API_URL}/promotions/wallet/budgets`).then((r) => r.json()).catch(() => ({ data: [] })),
      fetch(`${API_URL}/promotions/configurations`).then((r) => r.json()).catch(() => ({ data: [] })),
      fetch(`${API_URL}/promotions/analytics/enhanced`).then((r) => r.json()).catch(() => ({ data: { usageStats: [], summary: {} } })),
    ]).then(([summaryRes, budgetsRes, configsRes, analyticsRes]) => {
      // wallet/summary 404s until the wallet row is seeded — treat that as "$0, not yet funded"
      // rather than an error, matching what the live screen showed before any funding happened.
      setWalletSummary(summaryRes?.data || { total_transferred: 0, current_balance: 0 });
      setBudgets(budgetsRes.data || []);
      setConfigs(configsRes.data || []);
      setAnalytics(analyticsRes.data || { usageStats: [], summary: {} });
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const budgetFor = (promoId, configType) =>
    budgets.find((b) => b.promo_id === promoId && b.config_type === configType);

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <h1 className="mb-1 flex items-center gap-2 text-3xl font-bold text-gray-800 dark:text-white">
          <Gift size={28} /> Enhanced Promotion Management
        </h1>
        <p className="mb-6 text-gray-600 dark:text-gray-400">Manage and monitor all promotional campaigns in one place</p>

        {/* Promotion Wallet Summary */}
        <div className="mb-6 flex items-center justify-between rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
          <div>
            <p className="text-xs uppercase tracking-wide opacity-80">Total Transferred</p>
            <p className="text-2xl font-bold">{formatMoney(walletSummary.total_transferred)}</p>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wide opacity-80">Current Balance</p>
            <p className="text-2xl font-bold">{formatMoney(walletSummary.current_balance)}</p>
          </div>
          <button
            onClick={() => setShowFundModal(true)}
            className="flex items-center gap-2 rounded-lg bg-white/20 px-4 py-2 font-medium hover:bg-white/30"
          >
            <DollarSign size={16} /> Fund Wallet
          </button>
        </div>

        {/* Individual Promotion Budget Tracking */}
        <h2 className="mb-3 text-xl font-bold text-gray-800 dark:text-white">Individual Promotion Budget Tracking</h2>
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {budgets.map((b) => {
            const pct = b.total_allocated > 0 ? (b.spent / b.total_allocated) * 100 : 0;
            return (
              <div key={`${b.promo_id}-${b.config_type}`} className="rounded-lg border p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-bold uppercase">{b.config_type}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${b.status === "active" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                    {b.status}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><p className="text-gray-500">Allocated</p><p className="font-semibold">{formatMoney(b.total_allocated)}</p></div>
                  <div><p className="text-gray-500">Spent</p><p className="font-semibold text-red-600">{formatMoney(b.spent)}</p></div>
                  <div><p className="text-gray-500">Remaining</p><p className="font-semibold text-green-600">{formatMoney(b.remaining)}</p></div>
                </div>
                <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
                  <div className="h-1.5 rounded-full bg-blue-600" style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
              </div>
            );
          })}
          {budgets.length === 0 && !loading && <p className="text-gray-500">No budgets allocated yet.</p>}
        </div>

        {/* Promotion Configurations */}
        <h2 className="mb-3 text-xl font-bold text-gray-800 dark:text-white">Promotion Configurations</h2>
        <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {configs.map((c) => {
            const budget = budgetFor(c.promo_id, c.config_type);
            return (
              <div key={`${c.promo_id}-${c.config_type}`} className="rounded-lg border p-4">
                <div className="mb-2 flex items-center justify-between">
                  <span className="font-bold">{c.promo_id}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${c.is_active ? "bg-green-100 text-green-800" : "bg-gray-200 text-gray-600"}`}>
                    {c.is_active ? "Active" : "Inactive"}
                  </span>
                </div>
                <p className="mb-1 text-center text-2xl font-bold">{formatMoney(c.reward_amount)}</p>
                <p className="mb-3 text-center text-xs text-gray-500">Reward Amount</p>
                {budget && (
                  <div className="mb-3 flex justify-between rounded-lg bg-gray-50 p-2 text-xs dark:bg-gray-700">
                    <span>Allocated: <b>{formatMoney(budget.total_allocated)}</b></span>
                    <span>Remaining: <b className="text-red-600">{formatMoney(budget.remaining)}</b></span>
                  </div>
                )}
                <button
                  onClick={() => setEditingConfig(c)}
                  className="w-full rounded-lg bg-blue-600 py-2 text-sm font-medium text-white"
                >
                  Edit Configuration
                </button>
              </div>
            );
          })}
        </div>

        {/* Promotion Analytics */}
        <h2 className="mb-3 text-xl font-bold text-gray-800 dark:text-white">Promotion Analytics</h2>
        <div className="mb-6 overflow-x-auto rounded-lg border">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-100 text-xs uppercase text-gray-700 dark:bg-gray-700 dark:text-gray-300">
              <tr>
                <th className="px-4 py-2">Promotion Type</th>
                <th className="px-4 py-2">Total Rewards</th>
                <th className="px-4 py-2">Unique Users</th>
                <th className="px-4 py-2">Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {(analytics.usageStats || []).length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-3 text-center text-gray-500">
                  No usage data yet — analytics/enhanced is a stub endpoint (routes/promotions.js), not wired to real usage tables.
                </td></tr>
              ) : (
                analytics.usageStats.map((row, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-4 py-2">{row.promotion_type}</td>
                    <td className="px-4 py-2">{row.total_rewards}</td>
                    <td className="px-4 py-2">{row.unique_users}</td>
                    <td className="px-4 py-2">{formatMoney(row.total_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:bg-blue-900/20 dark:text-blue-200">
          <Info size={18} className="mt-0.5 shrink-0" />
          <div>
            <p className="mb-1 font-semibold">Note on leaderboards</p>
            <p>GET /promotions/leaderboard/current/driver and /passenger are hardcoded stubs on the backend
              (always return 2 fake "John Doe" rows) — not wired to real trip/earnings data. Not shown here
              rather than displaying fake names as if real.</p>
          </div>
        </div>
      </div>

      {editingConfig && (
        <EditConfigModal
          config={editingConfig}
          onClose={() => setEditingConfig(null)}
          onSaved={() => { setEditingConfig(null); loadAll(); }}
        />
      )}
      {showFundModal && (
        <FundWalletModal
          configs={configs}
          onClose={() => setShowFundModal(false)}
          onFunded={() => { setShowFundModal(false); loadAll(); }}
        />
      )}
    </DashboardLayout>
  );
};

export default EnhancedPromotions;
